import { inject, Injectable, SecurityContext } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from './auth.service';
import { FormField } from '../components/dynamic-form/dynamic-form.interface';


@Injectable({
  providedIn: 'root',
})
export class EventsService {
  private apiUrl = environment.apiUrl;
  private apiUrlClient = environment.apiUrl.replace('/public', '/client');

  private httpClient = inject(HttpClient);
  private authService = inject(AuthService);
  private sanitizer = inject(DomSanitizer);

  private headers = new HttpHeaders({
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  });

  async storeSession(advisor: string, data: { date: string; start: string; end: string }) {
    const headers = this.authService.getAuthHeaders();

    return firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/sessions/advisor/${advisor}`, data, { headers }).pipe(
        map((response: any) => {
          return response.data;
        }),
      ),
    );
  }

  async getEventsFromApi(type: 'current' | 'past', query?: string) {
    return firstValueFrom(
      this.httpClient
        .get(`${this.apiUrl}/events?type=${type}&paginate=disabled&query=${query || ''}`)
        .pipe(
          map((response: any) => {
            const data = response.data;
            return data;
          }),
        ),
    );
  }

  async getEventsPaginatedFromApi(
    type: 'current' | 'past',
    page: number,
    pageSize: number = 6,
    query?: string,
  ) {
    return firstValueFrom(
      this.httpClient
        .get(`${this.apiUrl}/events?type=${type}&page=${page}&pageSize=${pageSize}&query=${query || ''}`)
        .pipe(
          map((response: any) => {
            const data = response.data;
            const totalPages = response.meta.last_page;
            const currentPage = response.meta.current_page;
            const total = response.meta.total;
            return {
              data,
              totalPages,
              currentPage,
              total,
            };
          }),
        ),
    );
  }

  async getPublicEventById(eventId: string) {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/events/${eventId}`).pipe(
        map((response: any) => {
          const data = response.data;
          return data;
        }),
      ),
    );
  }

  async getClientEventById(eventId: string) {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrlClient}/events/${eventId}`).pipe(
        map((response: any) => {
          const data = response.data;
          return data;
        }),
      ),
    );
  }

  async storeEvent(eventId: string) {
    const headers = this.authService.getAuthHeaders();

    return firstValueFrom(
      this.httpClient.post(`${this.apiUrlClient}/events/${eventId}`, {}, { headers }).pipe(
        map((response: any) => {
          return response.data;
        }),
      ),
    );
  }

  async getClientEventsFromApi() {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrlClient}/events?paginate=disabled`).pipe(
        map((response: any) => {
          const data = response.data;
          return data;
        }),
      ),
    );
  }

  async payEvent(
    clientEventId: string,
    paymentData: {
      payment_number: string;
      payment_name: string;
      payment_bank: string;
    },
  ) {
    const headers = this.authService.getAuthHeaders();
    return firstValueFrom(
      this.httpClient
        .patch(`${this.apiUrlClient}/events/${clientEventId}/pay`, paymentData, { headers })
        .pipe(
          map((response: any) => {
            return response.data;
          }),
        ),
    );
  }

  async storeSatisfactionForm(sessionId: string, data: Record<string, any>) {
    const headers = this.authService.getAuthHeaders();
    return firstValueFrom(
      this.httpClient
        .post(`${this.apiUrl}/events/${sessionId}/satisfaction`, data, { headers })
        .pipe(map((response: any) => response.data)),
    );
  }

  async getBankQRData() {
    const timestamp = new Date().getTime();
    const headers = this.headers;

    const [bankResponse, qrResponse] = await Promise.all([
      firstValueFrom(this.httpClient.get(`${this.apiUrl}/extra-datas?key=events_bank&t=${timestamp}`, { headers })),
      firstValueFrom(this.httpClient.get(`${this.apiUrl}/extra-datas?key=events_qr&t=${timestamp}`, { headers }))
    ]);

    return {
      data: {
        value: (bankResponse as any).data.value,
        file: (qrResponse as any).data.file,
      }
    };
  }
}
